import { useCallback, useRef, useState, useEffect } from "react";
import { Chip } from '@progress/kendo-react-buttons';
import { useInternationalization } from "@progress/kendo-react-intl";
import { dateToFormattedString } from "../../utils/convert";
import eventEmitter from '../../utils/eventEmitter';
import "./FilterList.scss";
import Button from "../../buttons/Button";
import { SafeSVG } from "../../svg/SafeSVG";
import { buildUrl, isDateType } from "../../utils/helper";
import InputComponent from "../../forms/Input";
import ButtonComponent from "../../buttons/Button";

const operatorTranslations = {
  eq: '=',
  neq: '≠',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  contains: 'obsahuje',
  doesnotcontain: 'neobsahuje',
  startswith: 'začína s',
  endswith: 'končí s',
  isempty: "je prázdne",
  isnotempty: "nie je prázdne",
  isnull: "je prázdne",
  isnotnull: "nie je prázdne",
  between: 'medzi',
  exists: 'existuje záznam',
  notexists: 'neexistuje záznam',
  in: 'je v zozname',
  notin: 'nie je v zozname'
};

const FilterList = ({ primaryField, name, gridSchema, filter, userFilter, predefinedFilter, frms, removeFilter, getSchema, db, remove = true, onChangeFTS, getList, isFts, settedFTS }) => {
  const intl = useInternationalization();
  const scrollContainerRef = useRef(null);
  const [showButtons, setShowButton] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [showFilter, setShowFilter] = useState([]);
  const [isGrid, setIsGrid] = useState(true);
  const [fts, setFts] = useState("");
  const schemaCacheRef = useRef({});

  const groupByFilterId = useCallback((filters = []) => {
    const seenIds = new Set();
    const newFilters = [];
    for (const f of filters) {
      if (f.filterType === 1) continue;
      if (f.filterId) {
        if (seenIds.has(f.filterId)) {
          continue;
        }
        seenIds.add(f.filterId);
      }
      newFilters.push(f);
    }
    return newFilters;
  }, []);

  const groupByParentheses = (filters) => {
    const result = [];
    const stack = [result];

    filters.forEach(filter => {
      const leftParens = filter.left ? filter.left.split('').filter(c => c === '(').length : 0;
      const rightParens = filter.right ? filter.right.split('').filter(c => c === ')').length : 0;
      for (let i = 0; i < leftParens; i++) {
        const newGroup = [];
        stack[stack.length - 1].push({ type: 'group', filters: newGroup });
        stack.push(newGroup);
      }

      stack[stack.length - 1].push(filter);

      for (let i = 0; i < rightParens; i++) {
        if (stack.length > 1) {
          stack.pop();
        } else {
          console.warn("Nesprávne zátvorkovanie: pokus o ukončenie neexistujúcej skupiny", filter);
        }
      }
    });
    return result;
  };

  const getShowFilter = useCallback(async () => {
    let showFilter = await Promise.all(groupByFilterId(filter.filters)
      .map(async (f, index) => {
        if (f.filterId) {
          const uFilter = [...userFilter, ...predefinedFilter].find(x => x.id === f.filterId);
          return {
            id: index,
            name: uFilter.name,
            field: f.field,
            filterId: f.filterId,
            filterType: uFilter.type
          };
        }
        let schemaItem = gridSchema.find(x => x.field === f.field || (x.filter === "container" && x.field === (f.field?.split(".").slice(0, -1).join(".") || f.field)));
        let frmName;
        if (f.frmPod && frms?.length > 0) {
          const form = frms.find(x => x.Id === f.frmPod);
          frmName = form.Name;

          let formSchema = schemaCacheRef.current[form.Identif];
          if (!formSchema) {
            formSchema = await getSchema(form.Identif, db);
            schemaCacheRef.current[form.Identif] = formSchema;
          }
          schemaItem = formSchema.Grid.find(x => x.field === f.field || (x.filter === "container" && x.field === (f.field?.split(".").slice(0, -1).join(".") || f.field)));
        }

        return {
          id: index,
          field: f.field,
          title: schemaItem?.title,
          operator: f.operator,
          value: f.value,
          type: schemaItem?.filter,
          frm: frmName,
          //enum
          enumData: schemaItem?.enumData,
          primaryField: schemaItem?.primaryField,
          textField: schemaItem?.textField,
          //join
          join: f.join,
          left: f.left,
          right: f.right
        };
      }));

    setShowFilter(showFilter);
  }, [gridSchema, filter.filters, frms, getSchema, groupByFilterId, db, userFilter, predefinedFilter]);

  useEffect(() => {
    getShowFilter();
  }, [getShowFilter]);


  const handleRemove = (item) => {
    let filters = filter.filters.filter(x => !(x.field === item.field && item.value === x.value && item.operator === x.operator && x.filterType !== 1));
    if (item.filterId) {
      filters = filter.filters.filter(x => !(x.filterId === item.filterId));
    }
    const newFilter = { ...filter, filters: filters };
    removeFilter(newFilter);
  };

  const handleRemoveGroup = (groupData) => {
    let filters = filter.filters;
    groupData.filters.forEach(item => {
      filters = filters.filter(x => !(x.field === item.field && item.value === x.value && item.operator === x.operator));
      if (item.filterId) {
        filters = filters.filter(x => !(x.filterId === item.filterId));
      }
    });
    const newFilter = { ...filter, filters: filters };
    removeFilter(newFilter);
  };

  const buildFilterValue = (dataItem) => {
    let value = `"${dataItem.value}"`;
    if (dataItem.type === "boolean") {
      value = `${dataItem.value ? "Áno" : "Nie"}`;
    }
    else if (dataItem.type === "numeric") {
      value = `${dataItem.value}`;
    }
    else if (['enum', 'radio', 'formdatatable'].includes(dataItem.type)) {
      value = dataItem.enumData?.find(x => x[dataItem.primaryField] === dataItem.value)?.[dataItem.textField];
    }
    else if (isDateType(dataItem.type)) {
      if (dataItem.operator === "between") {
        if (!dataItem.value?.start || !dataItem.value.end) {
          //TODO: zobrazí sa nevalídny filter, ktorý nebude fungovať
        };
        const start = dateToFormattedString(dataItem.value?.start, "date", null, intl);
        const end = dateToFormattedString(dataItem.value?.end, "date", null, intl);
        value = `${start} - ${end}`;
      }
      else {
        // const date = dateToFormattedString(dataItem.value, "date", "dd.MM.yyyy", intl);
        // kedze je pouzity default format netreba ho specifikovat, ak je specifikovany je pouzity na preklad intl
        const date = dateToFormattedString(dataItem.value, "date", null, intl);
        value = `${date}`;
      }
    }

    if (["isempty", "isnotempty", "isnull", "isnotnull", "exists", "notexists"].includes(dataItem.operator)) {
      value = ``;
    }

    if (dataItem.name) { //User Filter
      value = dataItem.name;
    }

    return value;
  };

  const buildFilterIcon = (dataItem) => {
    switch (dataItem.filterType) {
      case 2: //Správcovský filter
      case 3: //Používateľský filter
        return "assets/icons/filter/pouzivatelsky.svg";
      case 4: //Inicializačný filter
      case 5: //Preddefinovaný filter
        return "assets/icons/filter/startovaci.svg";
      case 6: //Systémový filter s možnosťou vypnutia
        return "assets/icons/filter/systemovy.svg";
      case 1: //Systémový filter bez možnosti vypnutia  - nikdy na nezobrazí
      default:
        return null;
    }
  };

  const buildFilterTitle = (dataItem) => {
    let title = dataItem.title;
    if (dataItem.frm) {
      if (["exists", "notexists"].includes(dataItem.operator)) {
        title = dataItem.frm;
      }
      else {
        title = `${dataItem.frm} - ${title}`;
      }
    }
    return title;
  }

  const buildFilterChip = (dataItem) => {
    const title = buildFilterTitle(dataItem);
    const operator = operatorTranslations[dataItem.operator];
    const value = buildFilterValue(dataItem);
    const icon = buildFilterIcon(dataItem);

    return <Chip
      key={dataItem.id}
      fillMode={"outline"}
      rounded={"full"}
      size={"medium"}
    >
      <span className="k-chip-label">
        {icon && <SafeSVG src={buildUrl(icon)} />}
        {title && <span className="title">{title}</span>}
        {operator && <span className="operator">{operator}</span>}
        {value && <span className="value">{value}</span>}
      </span>
      {remove && <span className="k-chip-actions" onClick={() => handleRemove(dataItem)}>
        <span className="k-chip-action k-chip-remove-action">
          <span className="k-icon k-font-icon k-i-x" >
          </span>
        </span>
      </span>}
    </Chip>;
  };

  const FilterBubble = ({ groupData }) => {
    // Ak ide o skupinu, vykreslíme vnorené bubliny alebo filtre
    if (groupData.type === 'group') {
      return (
        <Chip fillMode="outline" rounded="full" size={"medium"} className="filter-bubble">
          <span className="k-chip-label">
            <span className="title">Pokročilý filter</span>
          </span>
          {remove && <span className="k-chip-actions" onClick={() => handleRemoveGroup(groupData)}>
            <span className="k-chip-action k-chip-remove-action">
              <span className="k-icon k-font-icon k-i-x" >
              </span>
            </span>
          </span>
          }
          {/* <span className="k-chip-label">
            {groupData.filters.map((item, idx) => (
              <FilterBubble key={idx} groupData={item} level={level + 1} />
            ))}
          </span>
          {remove && level === 0 && <span className="k-chip-actions" onClick={() => handleRemoveGroup(groupData)}>
            <span className="k-chip-action k-chip-remove-action">
              <span className="k-icon k-font-icon k-i-x" >
              </span>
            </span>
          </span>} */}
        </Chip>
      );
    }
    // Ak ide o jednotlivý filter, použijeme existujúcu funkciu na zobrazenie filtra
    return buildFilterChip(groupData);
  };

  const scroll = (right) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: right ? 300 : -300,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const onChangeTab = ({ name, tab }) => {
      if (name === primaryField) {
        setIsGrid(tab === 0);
      }
    }

    eventEmitter.on('CHANGE_TAB', onChangeTab);

    return () => {
      eventEmitter.off('CHANGE_TAB', onChangeTab);
    };
  }, [primaryField]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const checkOverflow = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      setShowButton(hasOverflow);

      const scrollLeft = el.scrollLeft;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };

    const handleScroll = () => {
      const scrollLeft = el.scrollLeft;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    el.addEventListener('scroll', handleScroll);
    resizeObserver.observe(el);
    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const confrimFTS = () => {
    onChangeFTS(fts);
    getList();
  };

  const removeFTS = () => {
    setFts("");
    onChangeFTS("");
    getList();
  }

  const filterGroups = groupByParentheses(showFilter);

  return (
    <div className='grid-info-row'>
      <div className='grid-info-row-name'>
        {name}
      </div>

      {isGrid && (
        <>
          {showButtons && <Button icon="chevron-left" onClick={() => scroll(false)} disabled={isAtStart} />}
          <div className="grid-info-row-filters" ref={scrollContainerRef}>
            {filterGroups.map((groupItem, idx) => (
              <FilterBubble key={idx} groupData={groupItem} />
            ))}
          </div>
          {showButtons && <Button icon="chevron-right" onClick={() => scroll(true)} disabled={isAtEnd} />}

          {isFts && (
            <form className={`fts ${fts === settedFTS && fts !== "" ? "fts-active" : ""}`} onSubmit={confrimFTS} autoComplete="off">
              <InputComponent
                name="fts"
                onChange={(_, value) => setFts(value)}
                value={fts}
                placeholder="Full-textové vyhľadávanie"
                //test
                title="FilterListInputTest"
              />
              {fts === settedFTS && fts !== "" &&
                <div className={`k-icon k-font-icon k-i-close`} onClick={removeFTS}></div>
              }
              <ButtonComponent
                onClick={confrimFTS}
                icon="search"
                // imageUrl={buildUrl('assets/icons/toolbar_fulltext.svg')}
                title="Vyhľadať"
                disabled={false} />
            </form>)}
        </>
      )}


    </div>
  );
};

export default FilterList;